import React, { useState } from "react";

import {
  Backdrop,
  CircularProgress,
  DialogContentText,
  makeStyles,
} from "@material-ui/core";

import { Confirm, SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../utils/appUtils";
import {
  buildCarrierIdentityFields,
  clearCarrierIdentityCache,
  normalizeCarrierIdentifiers,
  validateCarrierUnicity,
} from "../../../utils/carrierIdentity";
import { publishCreatedCarrier } from "../../../utils/carrierListCache";
import { clearCarrierUnicityMeta } from "./carrierUtils";
import {
  notifyPrimaryInviteResult,
  sendPrimaryDispatchInvite,
} from "./primaryDispatchInvite";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const ValidateCreateButton = (props) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleValidateSave = async () => {
    let messages = [];
    // The blur checks' cached verdicts do not get a vote here: a stale one
    // would keep the form invalid and stop the fresh check below from running.
    clearCarrierUnicityMeta(form);
    const formState = form.getState();
    const formData = formState.values;
    const isValid = !formState.invalid;
    setIsSaving(true);

    if (isValid) {
      // if valid, check the carrier is not already in the yard's book
      const isDuplicate = await validateCarrierUnicity(formData);

      if (isDuplicate) {
        notify(isDuplicate.message, "warning");
        setIsSaving(false);
        return; // Halt submission if duplicate found
      }
    }

    if (!isValid) {
      setIsSaving(false);
      // Submit anyway: the save is blocked client-side while the form is
      // invalid, but submitting is what marks the untouched fields so each one
      // says what it still needs. Without it, a blank required row (an empty ID
      // Numbers scheme or value) only flashes the backdrop and reports nothing.
      // The sibling create paths all submit-to-surface-errors the same way.
      handleSave();
      return; // return to edit form
    }

    // If we get here, validation passed and no duplicates found
    // Add confirmation message if needed
    if (messages.length > 0) {
      // Open Confirm dialog.
      setMessage(
        <DialogContentText>
          {messages.map((msg, index) => (
            <React.Fragment key={index}>{msg}</React.Fragment>
          ))}
        </DialogContentText>
      );
      setOpen(true);
    } else {
      // No confirmation needed, proceed directly to save
      handleSave();
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setIsSaving(false);
  };

  const handleDialogConfirm = async () => {
    setOpen(false);
    handleSave();
  };

  const handleSave = () => {
    props.handleSubmitWithRedirect();
  };

  const transform = (data) => {
    data = { ...data };
    data.active = "true";
    data.email = data.email.toLowerCase();
    data.reloadYardId = window.appUser?.reloadYardId;
    data.groups = window.appUser?.groups;

    // Written from the raw values on the same save, so the next uniqueness check
    // reads a normalised value that still matches the record it belongs to.
    Object.assign(data, buildCarrierIdentityFields(data));

    // Reduced to one row per scheme, blanks dropped. A form that never showed
    // the field leaves it undefined, which react-admin drops from the input.
    data.identifiers = normalizeCarrierIdentifiers(data.identifiers);

    return data;
  };

  const onSuccess = async ({ data }) => {
    try {
      // Keep the shared carrier list current, so a shipment form opened later
      // in this session offers the carrier instead of making the user reload.
      publishCreatedCarrier(data);
      // The book the on-blur checks read is now a carrier short of the truth.
      clearCarrierIdentityCache();
      notify(`Carrier company created successfully.`);
      // Onboarding: the carrier's own address becomes its primary dispatch
      // user. The carrier stands whether or not the invite goes out — a
      // failure here is reported, and Send Invite on the carrier retries it.
      try {
        const result = await sendPrimaryDispatchInvite(data);
        notifyPrimaryInviteResult(notify, data, result);
      } catch (error) {
        logError(
          "managerApp:carriers:ValidateCreateButton:primaryInvite",
          error,
          false,
        );
        notify(
          "Carrier created, but the invite could not be sent. Use Send Invite on the carrier to retry.",
          "warning",
        );
      }
      redirect("/carriers");
    } catch (error) {
      logError(
        "managerApp:carriers:ValidateCreateButton:onSuccess",
        error,
        notify
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError(
      "managerApp:carriers:ValidateCreateButton:onFailure",
      error,
      notify
    );
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        {...props}
        submitOnEnter={false}
        undoable={false}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        title="Save Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
